<template>
  <div v-if="isOpen" class="modal-overlay">
    <div class="gm-panel gm-panel--raised modal-container">
      <div class="header">
        <div class="header-top">
          <h2 class="gm-title title">Create New Skill</h2>
          <button @click="closeModal" class="close-btn" title="Close Skill Creation Modal">
            ×
          </button>
        </div>
        <p class="modal-description">Fill in the details to create a comprehensive skill with all properties.</p>
      </div>
      <form @submit.prevent="createSkill" class="skill-form gm-scroll">
          <!-- Skill Templates -->
          <div class="form-section">
            <h3 class="gm-section-title">Skill Templates</h3>
            <p class="template-description">Choose a pre-defined template to quickly create balanced skills, or start
              from scratch.</p>

            <div class="template-buttons">
              <button type="button" @click="applyTemplate('healing')" class="template-btn healing">
                <span class="template-icon">💚</span>
                <div class="template-info">
                  <strong>Healing Skill</strong>
                  <small>Concentration-based, restores health/energy</small>
                </div>
              </button>

              <button type="button" @click="applyTemplate('physical')" class="template-btn physical">
                <span class="template-icon">⚔️</span>
                <div class="template-info">
                  <strong>Physical Damage</strong>
                  <small>Physical Strength-based attack</small>
                </div>
              </button>

              <button type="button" @click="applyTemplate('magic')" class="template-btn magic">
                <span class="template-icon">🔮</span>
                <div class="template-info">
                  <strong>Magic Damage</strong>
                  <small>Flow Manipulation-based spell</small>
                </div>
              </button>

              <button type="button" @click="applyTemplate('forbidden')" class="template-btn forbidden">
                <span class="template-icon">💀</span>
                <div class="template-info">
                  <strong>Forbidden Magic</strong>
                  <small>High damage, health cost</small>
                </div>
              </button>

              <button type="button" @click="clearForm" class="template-btn clear">
                <span class="template-icon">🗑️</span>
                <div class="template-info">
                  <strong>Start Fresh</strong>
                  <small>Clear all fields</small>
                </div>
              </button>
            </div>
          </div>

          <!-- Basic Information -->
          <div class="form-section">
            <h3 class="gm-section-title">Basic Information</h3>

            <div class="gm-field">
              <label for="skillName" class="gm-label">Skill Name *</label>
              <input
                  id="skillName"
                  v-model="skillData.name"
                  type="text"
                  required
                  class="gm-input"
                  placeholder="Enter skill name"
              />
            </div>

            <div class="gm-field">
              <label for="skillDescription" class="gm-label">Description *</label>
              <textarea
                  id="skillDescription"
                  v-model="skillData.description"
                  class="gm-textarea"
                  placeholder="Enter skill description"
                  rows="3"
                  required
              ></textarea>
            </div>

            <div class="gm-field">
              <label for="skillSchool" class="gm-label">School (UUID)</label>
              <input
                  id="skillSchool"
                  v-model="skillData.school"
                  type="text"
                  class="gm-input"
                  placeholder="Enter school UUID (optional)"
              />
            </div>
          </div>

          <!-- Skill Properties -->
          <div class="form-section">
            <h3 class="gm-section-title">Properties</h3>

            <div class="form-row">
              <div class="gm-field">
                <label for="skillType" class="gm-label">Skill Type *</label>
                <select
                    id="skillType"
                    v-model="skillData.type"
                    class="gm-select"
                    required
                >
                  <option :value="SkillTypeEnum.Attack">Attack</option>
                  <option :value="SkillTypeEnum.Defense">Defense</option>
                  <option :value="SkillTypeEnum.Heal">Heal</option>
                  <option :value="SkillTypeEnum.Buff">Buff</option>
                  <option :value="SkillTypeEnum.Debuff">Debuff</option>
                  <option :value="SkillTypeEnum.Utility">Utility</option>
                  <option :value="SkillTypeEnum.Special">Special</option>
                </select>
              </div>

              <div class="gm-field">
                <label for="skillUse" class="gm-label">Use *</label>
                <select
                    id="skillUse"
                    v-model="skillData.use"
                    class="gm-select"
                    required
                >
                  <option :value="UseEnum.Self">Self</option>
                  <option :value="UseEnum.Any">Any</option>
                </select>
              </div>

              <div class="gm-field">
                <label for="skillGrade" class="gm-label">Grade *</label>
                <input
                    id="skillGrade"
                    v-model.number="skillData.grade"
                    type="number"
                    min="1"
                    max="10"
                    class="gm-input"
                    placeholder="1"
                    required
                />
              </div>
            </div>
          </div>

          <!-- Cost Configuration -->
          <div class="form-section">
            <CostConfiguration v-model="skillData.cost"/>
          </div>

          <!-- Effect Configuration -->
          <div class="form-section">
            <EffectConfiguration v-model="skillData.effect"/>
          </div>

          <!-- Impact Configuration -->
          <div class="form-section">
            <ImpactConfiguration v-model="skillData.impact"/>
          </div>

          <!-- Error Display -->
          <div v-if="error" class="error-message">
            {{ error }}
          </div>

          <!-- Success Display -->
          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <button type="button" class="gm-btn gm-btn--ghost" @click="closeModal" :disabled="isLoading">
              Cancel
            </button>
            <button type="button" class="gm-btn gm-btn--primary" @click="createSkill" :disabled="!isFormValid || isLoading">
              {{ isLoading ? 'Creating...' : 'Create Skill' }}
            </button>
          </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch} from 'vue'
import CostConfiguration from './CostConfiguration.vue'
import EffectConfiguration from './EffectConfiguration.vue'
import ImpactConfiguration from './ImpactConfiguration.vue'
import {GameMasterApi} from '@/api/backendService.js'
import {
  SkillTypeEnum,
  UseEnum,
  AttributesEnum,
  ImpactTypeEnum,
  ImpactViolationTypeEnum,
  CharacterStatEnum,
  type SkillCreateRequest,
  type CostRequest,
  type AssignableEffectRequest,
  type ImpactRequest
} from '@/api/dx-backend'

// Props
interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
  close: []
  skillCreated: [skill: any]
}>()

// Reactive state
const isLoading = ref(false)
const error = ref('')
const successMessage = ref('')

const skillData = ref<SkillCreateRequest>({
  name: '',
  description: '',
  school: '',
  type: SkillTypeEnum.Utility,
  use: UseEnum.Any,
  grade: 1,
  cost: [],
  effect: [],
  impact: []
})

// Computed
const isFormValid = computed(() => {
  return skillData.value.name.trim().length > 0 &&
      skillData.value.description.trim().length > 0
})

// Methods
const closeModal = () => {
  emit('close')
}

const resetForm = () => {
  skillData.value = {
    name: '',
    description: '',
    school: '',
      type: SkillTypeEnum.Utility,
    use: UseEnum.Any,
    grade: 1,
    cost: [],
    effect: [],
    impact: []
  }
  error.value = ''
  successMessage.value = ''
}

const clearForm = () => {
  resetForm()
}

const applyTemplate = (templateType: string) => {
  error.value = ''

  switch (templateType) {
    case 'healing':
      skillData.value = {
        name: 'Healing Touch',
        description: 'A restorative skill that heals wounds and restores energy through concentrated flow manipulation.',
        school: '',
              type: SkillTypeEnum.Heal,
        grade: 9,
        cost: [
          {
            kind: AttributesEnum.ActionPoints,
            value: 5
          },
          {
            kind: AttributesEnum.Energy,
            value: 31
          }
        ],
        effect: [],
        impact: [
          {
            kind: ImpactTypeEnum.Heal,
            type: ImpactViolationTypeEnum.Energy,
            formula: {
              base: 13,
              requires: [
                {
                  stat: CharacterStatEnum.Concentration,
                  value: 10
                }
              ],
              scaling: [
                {
                  stat: CharacterStatEnum.Concentration,
                  value: 0.05
                }
              ],
              min_efficiency: 0.5,
              max_efficiency: 2.0
            }
          }
        ]
      }
      break

    case 'physical':
      skillData.value = {
        name: 'Power Strike',
        description: 'A devastating physical attack that channels raw strength into a focused blow.',
        school: '',
              type: SkillTypeEnum.Attack,
        grade: 9,
        cost: [
          {
            kind: AttributesEnum.ActionPoints,
            value: 2
          },
          {
            kind: AttributesEnum.Energy,
            value: 12
          }
        ],
        effect: [],
        impact: [
          {
            kind: ImpactTypeEnum.Damage,
            type: ImpactViolationTypeEnum.Physical,
            formula: {
              base: 6,
              requires: [
                {
                  stat: CharacterStatEnum.PhysicalStrength,
                  value: 10
                }
              ],
              scaling: [
                {
                  stat: CharacterStatEnum.FlowResonance,
                  value: 0.05
                }
              ],
              min_efficiency: 0.5,
              max_efficiency: 2.0
            }
          }
        ]
      }
      break

    case 'magic':
      skillData.value = {
        name: 'Arcane Bolt',
        description: 'A focused magical projectile that harnesses flow manipulation to deal energy damage.',
        school: '',
              type: SkillTypeEnum.Attack,
        grade: 9,
        cost: [
          {
            kind: AttributesEnum.ActionPoints,
            value: 3
          },
          {
            kind: AttributesEnum.Energy,
            value: 25
          }
        ],
        effect: [],
        impact: [
          {
            kind: ImpactTypeEnum.Damage,
            type: ImpactViolationTypeEnum.Energy,
            formula: {
              base: 12,
              requires: [
                {
                  stat: CharacterStatEnum.FlowManipulation,
                  value: 10
                }
              ],
              scaling: [
                {
                  stat: CharacterStatEnum.FlowManipulation,
                  value: 0.05
                }
              ],
              min_efficiency: 0.5,
              max_efficiency: 2.0
            }
          }
        ]
      }
      break

    case 'forbidden':
      skillData.value = {
        name: 'Soul Rend',
        description: 'A forbidden technique that sacrifices the caster\'s life force to unleash devastating magical damage.',
        school: '',
              type: SkillTypeEnum.Attack,
        grade: 9,
        cost: [
          {
            kind: AttributesEnum.ActionPoints,
            value: 3
          },
          {
            kind: AttributesEnum.Energy,
            value: 10
          },
          {
            kind: AttributesEnum.Health,
            value: 9
          }
        ],
        effect: [],
        impact: [
          {
            kind: ImpactTypeEnum.Damage,
            type: ImpactViolationTypeEnum.Energy,
            formula: {
              base: 20,
              requires: [
                {
                  stat: CharacterStatEnum.FlowResonance,
                  value: 10
                }
              ],
              scaling: [
                {
                  stat: CharacterStatEnum.FlowResonance,
                  value: 0.05
                }
              ],
              min_efficiency: 0.5,
              max_efficiency: 2.0
            }
          }
        ]
      }
      break

    default:
      resetForm()
      break
  }
}

const createSkill = async () => {
  if (!isFormValid.value || isLoading.value) return

  isLoading.value = true
  error.value = ''

  try {
    // Prepare skill data for API according to SkillCreateRequest interface
    const skillPayload: SkillCreateRequest = {
      name: skillData.value.name.trim(),
      description: skillData.value.description.trim(),
      school: skillData.value.school?.trim() || undefined,
      type: skillData.value.type,
      grade: skillData.value.grade,
      cost: skillData.value.cost?.length ? skillData.value.cost : undefined,
      effect: skillData.value.effect?.length ? skillData.value.effect : undefined,
      impact: skillData.value.impact?.length ? skillData.value.impact : undefined
    }

    // Call the GameMaster API to create the skill
    const response = await GameMasterApi.gamemasterSkillNewSkillCreate(skillPayload)

    // Show success message
    successMessage.value = `Skill "${skillPayload.name}" has been created successfully!`

    // Emit success event
    emit('skillCreated', response.data)

    // Reset form after a short delay to show success message
    setTimeout(() => {
      resetForm()
    }, 3000)

  } catch (err: any) {
    console.error('Error creating skill:', err)

    // Clear any existing success message
    successMessage.value = ''

    // Provide specific error messages based on error type
    if (err.response) {
      // Server responded with error status
      const status = err.response.status
      const data = err.response.data

      if (status === 401) {
        error.value = 'Authentication failed. Please log in again.'
      } else if (status === 403) {
        error.value = 'You do not have permission to create skills.'
      } else if (status === 400) {
        // Validation errors
        if (data?.detail) {
          error.value = `Validation error: ${data.detail}`
        } else if (data?.message) {
          error.value = data.message
        } else {
          error.value = 'Invalid skill data. Please check your inputs.'
        }
      } else if (status === 500) {
        error.value = 'Server error occurred. Please try again later.'
      } else {
        error.value = data?.message || `Server error (${status}). Please try again.`
      }
    } else if (err.request) {
      // Network error
      error.value = 'Network error. Please check your connection and try again.'
    } else {
      // Other error
      error.value = 'An unexpected error occurred. Please try again.'
    }
  } finally {
    isLoading.value = false
  }
}

// Watch for modal open/close to reset form
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    resetForm()
  }
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  display: flex;
  flex-direction: column;
  width: 42rem;
  max-width: 95vw;
  max-height: 90vh;
  padding: var(--gm-gap-lg);
}

.header {
  flex-shrink: 0;
  margin-bottom: var(--gm-gap);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.7rem;
  position: relative;
}

.title {
  margin: 0;
  flex: 1;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--gm-border-dim);
  border-radius: 50%;
  background: var(--gm-surface-sunken);
  color: var(--gm-text-muted);
  font-family: var(--gm-font-display);
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease, border-color 0.15s ease;
  line-height: 1;
}

.close-btn:hover {
  border-color: var(--gm-border);
  color: var(--gm-accent);
}

.modal-description {
  margin: 0 0 var(--gm-gap-sm) 0;
  color: var(--gm-text-muted);
  font-size: 0.9rem;
  line-height: 1.4;
  text-align: center;
}

.skill-form {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
  overflow-y: auto;
  padding-right: 0.25rem;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.form-section .gm-section-title {
  border-bottom: 1px solid var(--gm-border-dim);
  padding-bottom: 0.375rem;
  margin-bottom: 0;
}

.form-row {
  display: flex;
  gap: var(--gm-gap);
}

.form-row .gm-field {
  flex: 1;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  color: var(--gm-text);
  cursor: pointer;
  font-size: 0.9rem;
}

.checkbox-label input[type="checkbox"] {
  margin: 0;
  accent-color: var(--gm-accent);
}

.error-message {
  background: rgba(201, 69, 63, 0.12);
  border: 1px solid rgba(201, 69, 63, 0.4);
  border-radius: var(--gm-radius-sm);
  padding: 0.75rem;
  color: var(--gm-danger);
  font-size: 0.875rem;
}

.success-message {
  background: var(--gm-accent-soft);
  border: 1px solid var(--gm-border);
  border-radius: var(--gm-radius-sm);
  padding: 0.75rem;
  color: var(--gm-accent);
  font-size: 0.875rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gm-gap-sm);
  margin-top: var(--gm-gap-sm);
  padding-top: var(--gm-gap-sm);
  border-top: 1px solid var(--gm-border-dim);
}

.template-description {
  color: var(--gm-text-muted);
  margin: 0 0 var(--gm-gap-sm) 0;
  font-size: 0.875rem;
  line-height: 1.4;
}

.template-buttons {
  display: flex;
  flex-direction: row;
  gap: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap-sm);
  justify-content: space-between;
}

.template-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.3rem;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  background: var(--gm-surface-sunken);
  color: var(--gm-text);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  text-align: center;
  font-size: 0.75rem;
  flex: 1;
  min-width: 0;
}

.template-btn:hover {
  border-color: var(--gm-border);
  background: var(--gm-accent-soft);
  color: var(--gm-accent);
}

.template-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.template-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.template-info strong {
  font-family: var(--gm-font-display);
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--gm-text);
}

.template-info small {
  font-size: 0.8rem;
  color: var(--gm-text-muted);
  line-height: 1.2;
}
</style>